import { useState, useEffect, useRef } from 'react';
import { MapPin, Eye, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MosqueMapView.scss';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MosqueMapView = () => {
    const navigate = useNavigate();
    const { confirm, error } = useSnackbar();
    const [mosques, setMosques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMosque, setSelectedMosque] = useState(null);
    const [mapView, setMapView] = useState('list'); // 'list' or 'map'
    const [year, setYear] = useState(2026);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);

    useEffect(() => {
        fetchMosques();
    }, [year]);

    useEffect(() => {
        if (mapView === 'map' && mosques.length > 0 && mapRef.current && !mapInstanceRef.current) {
            initializeMap();
        }
        
        return () => {
            // Cleanup map when component unmounts or view changes
            if (mapInstanceRef.current && mapView !== 'map') {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                markersRef.current = [];
            }
        };
    }, [mapView, mosques]);

    const fetchMosques = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/mosques', {
                params: { limit: 1000, year }
            });
            if (response.data.success) {
                const mosquesData = response.data.mosques;
                setMosques(mosquesData);
            }
        } catch (err) {
            error('Failed to fetch mosques');
            console.error('Error fetching mosques:', err);
        } finally {
            setLoading(false);
        }
    };

    const createNumberedIcon = (number) => {
        return L.divIcon({
            className: 'custom-numbered-marker',
            html: `<div class="marker-number">${number}</div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 30],
            popupAnchor: [0, -30]
        });
    };

    const initializeMap = () => {
        if (!mapRef.current) return;

        // Calculate center (average of all mosque locations or default to Kerala)
        const mosquesWithCoords = mosques.filter(m => m.location?.latitude && m.location?.longitude);
        
        let center = [10.8505, 76.2711]; // Default: Thrissur, Kerala [lat, lng]
        
        if (mosquesWithCoords.length > 0) {
            const avgLat = mosquesWithCoords.reduce((sum, m) => sum + m.location.latitude, 0) / mosquesWithCoords.length;
            const avgLng = mosquesWithCoords.reduce((sum, m) => sum + m.location.longitude, 0) / mosquesWithCoords.length;
            center = [avgLat, avgLng];
        }

        // Create map
        const map = L.map(mapRef.current, {
            center: center,
            zoom: 10,
            zoomControl: true
        });

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        // Clear existing markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];

        // Add markers for each mosque
        mosquesWithCoords.forEach((mosque, index) => {
            const position = [mosque.location.latitude, mosque.location.longitude];

            // Create marker with number
            const marker = L.marker(position, {
                icon: createNumberedIcon(index + 1)
            }).addTo(map);

            // Store mosqueID for easy reference
            marker.mosqueID = mosque.mosqueID;

            // Create popup content
            const popupContent = `
                <div class="map-popup">
                    <h3>${index + 1}. ${mosque.name}</h3>
                    <p><strong>District:</strong> ${mosque.district}</p>
                    <p><strong>Dish:</strong> ${getDishDisplayName(mosque.dish)}</p>
                    <p><strong>Status:</strong> ${mosque.status}</p>
                    ${mosque.description ? `<p>${mosque.description.substring(0, 100)}...</p>` : ''}
                    <a href="${mosque.googleMapsLink}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>
                </div>
            `;

            marker.bindPopup(popupContent);

            // Add click listener
            marker.on('click', () => {
                setSelectedMosque(mosque);
            });

            markersRef.current.push(marker);
        });

        // Fit bounds to show all markers
        if (mosquesWithCoords.length > 0) {
            const bounds = L.latLngBounds(mosquesWithCoords.map(m => [m.location.latitude, m.location.longitude]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    };

    const handleMosqueClick = (mosque) => {
        setSelectedMosque(mosque);
        
        // Pan to mosque on map if map is active
        if (mapView === 'map' && mapInstanceRef.current && mosque.location?.latitude) {
            const position = [mosque.location.latitude, mosque.location.longitude];
            mapInstanceRef.current.setView(position, 15);
            
            // Open popup for the marker
            const marker = markersRef.current.find(m => 
                m.mosqueID === mosque.mosqueID
            );
            if (marker) {
                marker.openPopup();
            }
        }
    };

    const handleDelete = async (mosqueID) => {
        confirm(
            'Are you sure you want to delete this mosque? This action cannot be undone.',
            async () => {
                try {
                    await api.delete(`/admin/mosques/${mosqueID}`);
                    setMosques(mosques.filter(m => m.mosqueID !== mosqueID));
                    // Remove marker from map
                    const markerIndex = markersRef.current.findIndex(m => 
                        m.mosqueID === mosqueID
                    );
                    if (markerIndex !== -1) {
                        markersRef.current[markerIndex].remove();
                        markersRef.current.splice(markerIndex, 1);
                    }
                } catch (err) {
                    error(err.response?.data?.message || 'Error deleting mosque');
                }
            }
        );
    };

    if (loading) {
        return <div className="loading">Loading mosques...</div>;
    }

    return (
        <div className="mosque-map-view">
            <div className="view-header">
                <h2>Mosques Map View</h2>
                <div className="view-controls">
                    <select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="year-filter"
                        title="Filter by year"
                    >
                        <option value={2026}>2026</option>
                        <option value={2025}>2025</option>
                    </select>
                    <div className="view-toggle">
                    <button
                        className={`toggle-btn ${mapView === 'list' ? 'active' : ''}`}
                        onClick={() => setMapView('list')}
                    >
                        List View
                    </button>
                    <button
                        className={`toggle-btn ${mapView === 'map' ? 'active' : ''}`}
                        onClick={() => {
                            setMapView('map');
                            if (!mapInstanceRef.current && mapRef.current) {
                                setTimeout(() => initializeMap(), 100);
                            }
                        }}
                    >
                        Map View
                    </button>
                    </div>
                </div>
            </div>

            <div className={`view-container ${mapView}`}>
                <div className="mosques-list-panel">
                    <div className="list-header">
                        <h3>{mosques.length} Mosques</h3>
                    </div>
                    <div className="mosques-list">
                        {mosques.map((mosque, index) => (
                            <div
                                key={mosque._id}
                                className={`mosque-item ${selectedMosque?._id === mosque._id ? 'selected' : ''}`}
                                onClick={() => handleMosqueClick(mosque)}
                            >
                                <div className="mosque-number">{index + 1}</div>
                                <div className="mosque-content">
                                    <h4>{mosque.name}</h4>
                                    <p className="mosque-location">
                                        <MapPin size={14} />
                                        {mosque.district}, {mosque.state}
                                    </p>
                                    <p className="mosque-dish">🍛 {getDishDisplayName(mosque.dish)}</p>
                                    <p className="mosque-description">
                                        {mosque.description ? 
                                            (mosque.description.length > 100 
                                                ? mosque.description.substring(0, 100) + '...' 
                                                : mosque.description)
                                            : 'No description'}
                                    </p>
                                    <div className="mosque-status">
                                        <span className={`status-badge ${mosque.status}`}>
                                            {mosque.status}
                                        </span>
                                        {mosque.verified && (
                                            <span className="verified-badge">Verified</span>
                                        )}
                                    </div>
                                </div>
                                <div className="mosque-actions">
                                    <button
                                        className="action-btn view"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/mosques/${mosque.mosqueID}`);
                                        }}
                                        title="View"
                                    >
                                        <Eye size={16} />
                                    </button>
                                    <button
                                        className="action-btn edit"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/mosques/${mosque.mosqueID}/edit`);
                                        }}
                                        title="Edit"
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        className="action-btn delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(mosque.mosqueID);
                                        }}
                                        title="Delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {mapView === 'map' && (
                    <div className="map-panel">
                        <div ref={mapRef} className="leaflet-map" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MosqueMapView;
